import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { fmtCurrency, fmtDate, isPackageOfferExpired, usePublicSite, useSettings } from "@/lib/utils-app";
import PackageAvailability from "@/components/PackageAvailability";
import HeroCarousel from "@/components/HeroCarousel";
import BranchBadge, { hasMultipleBranches } from "@/components/BranchBadge";

const GYM_INTERIOR = "https://images.pexels.com/photos/5750886/pexels-photo-5750886.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function statIcon(name) {
  const key = String(name || "trophy")
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join("");
  return Icons[key] || Icons.Trophy;
}

function Countdown({ target }) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const ms = new Date(target).getTime() - Date.now();
      if (ms <= 0) { setT({ d: 0, h: 0, m: 0, s: 0 }); return; }
      const d = Math.floor(ms / 86400000);
      const h = Math.floor((ms % 86400000) / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      setT({ d, h, m, s });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  return (
    <div className="flex gap-2 text-center" data-testid="countdown">
      {["d","h","m","s"].map((k, i) => (
        <div key={k} className="bg-black/60 border border-white/10 rounded-sm px-3 py-2 min-w-[58px]">
          <div className="font-display text-2xl text-[#FFD700]">{String(t[k]).padStart(2, "0")}</div>
          <div className="text-[10px] uppercase text-white/60 tracking-widest">{["Días","Hrs","Min","Seg"][i]}</div>
        </div>
      ))}
    </div>
  );
}

export default function Home() {
  const site = usePublicSite();
  useSettings();
  const [packages, setPackages] = useState([]);
  const [events, setEvents] = useState([]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    api.get("/packages").then(r => setPackages(r.data.slice(0, 3))).catch(() => {});
    api.get("/events").then(r => {
      const upcoming = r.data.filter(e => new Date(e.date) > new Date()).slice(0, 2);
      setEvents(upcoming);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const multiPkg = hasMultipleBranches(packages);
  const multiEv = hasMultipleBranches(events);
  const stats = site.home_stats || [];

  return (
    <div data-testid="hero-section">
      <section className="relative min-h-[88vh] overflow-hidden flex items-center">
        <HeroCarousel
          slides={site.hero_carousel_slides}
          intervalSec={site.hero_carousel_interval_sec}
        />
        <div className="absolute inset-0 z-[2] bg-gradient-to-r from-black/95 via-black/70 to-black/30" />
        <div className="absolute inset-0 z-[2] noise" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#E63946] mb-6">
              <Icons.Flame size={14} /> {site.hero_badge}
            </div>
            <h1 className="font-display text-6xl md:text-8xl leading-[0.9] uppercase text-white">
              {site.hero_title_prefix}{" "}
              <span className="text-[#E63946]">{site.hero_title_highlight}</span>
              {site.hero_title_suffix ? (
                <>
                  <br />{site.hero_title_suffix}
                </>
              ) : null}
            </h1>
            <p className="mt-6 text-lg text-white/60 max-w-xl">{site.hero_subtitle}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/paquetes" className="btn-primary" data-testid="hero-join-btn">
                {site.hero_btn_primary} <ChevronRight size={18} />
              </Link>
              <Link to="/paquetes" className="btn-outline" data-testid="hero-packages-btn">
                {site.hero_btn_secondary}
              </Link>
              <Link to="/contacto" className="btn-outline" data-testid="hero-contact-btn">
                {site.hero_btn_contact}
              </Link>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
            {stats.slice(0, 4).map((st, i) => {
              const Icon = statIcon(st.icon);
              return (
                <div key={i} className="glass p-4 rounded-sm">
                  <Icon className="text-[#FFD700]" size={20} />
                  <div className="font-display text-3xl text-white mt-1">{st.value}</div>
                  <div className="text-xs uppercase tracking-wider text-white/60">{st.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {packages.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#E63946]">{site.packages_eyebrow}</div>
              <h2 className="font-display text-5xl md:text-6xl text-white mt-2">
                {site.packages_title_prefix}{" "}
                <span className="text-[#FFD700]">{site.packages_title_highlight}</span>
              </h2>
            </div>
            <Link to="/paquetes" className="btn-outline text-sm">{site.packages_link_label}</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {packages.map((p, i) => {
              const offerExpired = isPackageOfferExpired(p, now);
              return (
              <div key={p.id} className={`glass p-8 rounded-sm relative ${i === 1 ? "border-[#FFD700]/40" : ""}`} data-testid="pricing-card">
                {i === 1 && (
                  <div className="absolute -top-3 left-6 bg-[#FFD700] text-black text-[10px] font-bold uppercase tracking-widest px-2 py-1">
                    {site.popular_badge_label}
                  </div>
                )}
                <div className="font-display text-3xl text-white">{p.name}</div>
                <BranchBadge name={p.branch_name} show={multiPkg} />
                <p className="text-sm text-white/60 mt-2 min-h-[40px]">{p.description}</p>
                <div className="mt-6 flex items-end gap-1">
                  <span className="font-display text-5xl text-[#E63946]">{fmtCurrency(p.monthly_cost)}</span>
                  <span className="text-sm text-white/60 mb-2">/mes</span>
                </div>
                {p.inscription_cost > 0 && (
                  <div className="text-xs text-white/40 mt-1">+ {fmtCurrency(p.inscription_cost)} inscripción</div>
                )}
                <PackageAvailability pkg={p} publicView />
                <ul className="mt-6 space-y-2">
                  {(p.benefits || []).slice(0, 5).map((b, j) => (
                    <li key={j} className="text-sm text-white/60 flex gap-2"><span className="text-[#E63946]">▸</span> {b}</li>
                  ))}
                </ul>
                {offerExpired ? (
                  <p className="mt-8 text-sm text-red-400/90 text-center">Oferta finalizada</p>
                ) : (
                <Link to="/paquetes" className="mt-8 btn-primary w-full justify-center text-sm">
                  Suscribirme
                </Link>
                )}
              </div>
              );
            })}
          </div>
        </section>
      )}

      {events.length > 0 && (
        <section className="border-y border-theme-secondary/10 bg-black/40 py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-[#E63946]">{site.events_eyebrow}</div>
                <h2 className="font-display text-5xl md:text-6xl text-white mt-2">
                  {site.events_title_prefix}{" "}
                  <span className="text-[#FFD700]">{site.events_title_highlight}</span>
                </h2>
              </div>
              <Link to="/eventos" className="btn-outline text-sm">{site.events_link_label}</Link>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {events.map((ev) => (
                <div key={ev.id} className="relative overflow-hidden rounded-sm border border-white/10 group" data-testid="event-card">
                  <div className="h-72 bg-cover bg-center" style={{ backgroundImage: `url(${ev.poster || GYM_INTERIOR})` }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="text-[10px] uppercase tracking-widest text-[#FFD700]">{ev.type}</div>
                    <h3 className="font-display text-3xl text-white mt-1">{ev.title}</h3>
                    <BranchBadge name={ev.branch_name} show={multiEv} />
                    <p className="text-sm text-white/60 mt-1">{fmtDate(ev.date)} · {ev.location}</p>
                    <div className="mt-4">
                      <Countdown target={ev.date} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20">
        <div className="relative glass-strong rounded-sm overflow-hidden p-8 md:p-16">
          <div className="absolute inset-0 noise opacity-50" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-display text-5xl text-white">
                {site.cta_title_prefix}{" "}
                <span className="text-[#E63946]">{site.cta_title_highlight}</span>
                {site.cta_title_suffix || ""}
              </h2>
              <p className="text-white/60 mt-4">{site.cta_subtitle}</p>
            </div>
            <div className="flex gap-4 flex-wrap">
              <Link to="/contacto" className="btn-gold">{site.cta_btn_primary}</Link>
              <Link to="/paquetes" className="btn-outline">{site.cta_btn_secondary}</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
